import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { toast } from 'sonner';
import { useBulkDeleteDonationsMutation, useDonationSummaryQuery } from '../../../../../queries';
import { BULK_DELETE_DONATION_LIMIT } from '../../../../../queries/Donation/useBulkDeleteDonations';
import { baseStyleSheet } from '../../../../styles/styles';
import { CloseButton } from '../../../CloseButton';
import { CustomDateRange } from '../../../CustomDateRange';
import { LoadingSpinner } from '../../../LoadingSpinner';
import { Modal } from '../../../Modal';
import { TextInput } from '../../../TextInput';
import { styleSheet } from './styles';

type Mode = 'range' | 'wipe';

const WIPE_ALL_CONFIRMATION = 'DELETE ALL';
const WIPE_ALL_FLOOR_DATE = '1900-01-01';

const buildWipeAllFilter = (): Api.IDonationFilterRequest => ({
	criteria: [
		{
			property: Api.DonationFilterProperty.CreationDate,
			op: Api.FilterOperator.Gt,
			value: WIPE_ALL_FLOOR_DATE,
		},
	],
});

const formatDateForFilter = (date: Date, endOfDay: boolean): string => {
	return endOfDay ? moment(date).endOf('day').toISOString() : moment(date).startOf('day').toISOString();
};

export interface DeletePreviousImportRange {
	startIso: string;
	endIso: string;
}

export const DeletePreviousImportModal = ({
	isOpen,
	onClose,
	onApplyRange,
	onWipedAll,
}: {
	isOpen: boolean;
	onClose: () => void;
	onApplyRange: (range: DeletePreviousImportRange) => void;
	onWipedAll?: () => void;
}) => {
	const [mode, setMode] = React.useState<Mode>('range');
	const [from, setFrom] = React.useState<Date | null>(null);
	const [to, setTo] = React.useState<Date | null>(null);
	const [typed, setTyped] = React.useState('');

	React.useEffect(() => {
		if (!isOpen) {
			setMode('range');
			setFrom(null);
			setTo(null);
			setTyped('');
		}
	}, [isOpen]);

	const wipeAllFilter = React.useMemo(() => buildWipeAllFilter(), []);

	const wipeAllCountQuery = useDonationSummaryQuery({
		enabled: isOpen && mode === 'wipe',
		filterRequest: wipeAllFilter,
	});

	const wipeAllCount = wipeAllCountQuery.data?.totalDonations ?? null;

	const bulkDeleteMutation = useBulkDeleteDonationsMutation({
		onSuccess: result => {
			const deleted = result?.deletedCount ?? wipeAllCount ?? 0;
			toast.success(`Archived ${deleted} donation${deleted === 1 ? '' : 's'}`);
			onWipedAll?.();
			onClose();
		},
		onError: () => {
			toast.error('Failed to delete donations. Please try again.');
		},
	});

	const isDeleting = bulkDeleteMutation.isPending;
	const wipeOverLimit = (wipeAllCount ?? 0) > BULK_DELETE_DONATION_LIMIT;
	const canSubmitRange = mode === 'range' && !!from && !!to && !isDeleting;
	const canSubmitWipe =
		mode === 'wipe' &&
		typed.trim().toUpperCase() === WIPE_ALL_CONFIRMATION &&
		(wipeAllCount ?? 0) > 0 &&
		!wipeOverLimit &&
		!isDeleting;

	const submit = () => {
		if (mode === 'range') {
			if (!canSubmitRange) return;
			onApplyRange({
				startIso: formatDateForFilter(from, false),
				endIso: formatDateForFilter(to, true),
			});
			onClose();
			return;
		}

		if (!canSubmitWipe) return;
		bulkDeleteMutation.mutate(wipeAllFilter);
	};

	return (
		<Modal isOpen={isOpen} onRequestClose={isDeleting ? undefined : onClose}>
			<div>
				<div className={css(styleSheet.closeButtonContainer)}>
					<CloseButton onClick={onClose} disabled={isDeleting} />
				</div>
				<div className={css(styleSheet.container)}>
					<h2 className={css(styleSheet.title, mode === 'wipe' ? styleSheet.titleDestructive : null)}>
						{mode === 'range' ? 'Delete a previous import' : 'Delete ALL donations'}
					</h2>

					<div className={css(styleSheet.modeSelector)}>
						<label className={css(styleSheet.modeOption)}>
							<input
								className={css(styleSheet.modeOptionRadio)}
								type='radio'
								name='delete-previous-import-mode'
								value='range'
								checked={mode === 'range'}
								onChange={() => setMode('range')}
								disabled={isDeleting}
							/>
							<div>
								<div className={css(styleSheet.modeOptionLabel)}>Delete a previous import</div>
								<div className={css(styleSheet.modeOptionDescription)}>
									Filter the donation board by when the records were created, then review and delete them.
								</div>
							</div>
						</label>
						<label className={css(styleSheet.modeOption)}>
							<input
								className={css(styleSheet.modeOptionRadio)}
								type='radio'
								name='delete-previous-import-mode'
								value='wipe'
								checked={mode === 'wipe'}
								onChange={() => setMode('wipe')}
								disabled={isDeleting}
							/>
							<div>
								<div className={css(styleSheet.modeOptionLabel)}>Delete ALL donations</div>
								<div className={css(styleSheet.modeOptionDescription)}>
									Permanently remove every donation in this account. Use this to start over from a clean slate.
								</div>
							</div>
						</label>
					</div>

					{mode === 'range' ? (
						<>
							<div className={css(styleSheet.body)}>
								Pick the date range covering your import. We&apos;ll filter the board to show only donations created
								during that window so you can review and delete them.
							</div>
							<div className={css(styleSheet.datePickerContainer)}>
								<CustomDateRange
									endDateRequired
									resetDates={() => {
										setFrom(null);
										setTo(null);
									}}
									from={from}
									to={to}
									onChange={(f, t) => {
										if (!f || !t) return;
										setFrom(f);
										setTo(t);
									}}
									showTextInput
									disableScrollTo
								/>
							</div>
						</>
					) : (
						<>
							<div className={css(styleSheet.warningBanner)}>
								<strong>Heads up.</strong> Every donation in this account will be archived. Their tag and campaign
								associations will be cleared too. Contact support if you later need to restore any.
							</div>
							<div className={css(styleSheet.body)}>
								{wipeAllCountQuery.isLoading ? (
									<>Checking how many donations are in this account…</>
								) : wipeAllCount === null ? (
									<>We couldn&apos;t load the donation count. Please try again.</>
								) : (
									<>
										This will delete{' '}
										<span className={css(styleSheet.count)}>
											{wipeAllCount} donation{wipeAllCount === 1 ? '' : 's'}
										</span>
										.
									</>
								)}
							</div>
							{wipeOverLimit ? (
								<div className={css(styleSheet.body)}>
									<strong>
										That&apos;s over the {BULK_DELETE_DONATION_LIMIT.toLocaleString()}-row limit for a single bulk
										delete.
									</strong>{' '}
									Use the per-import date range option above and delete in batches.
								</div>
							) : (
								<div className={css(styleSheet.inputWrap)}>
									<label className={css(styleSheet.label)} htmlFor='delete-all-donations-confirm-input'>
										Type <strong>{WIPE_ALL_CONFIRMATION}</strong> to confirm
									</label>
									<TextInput
										inputId='delete-all-donations-confirm-input'
										type='text'
										value={typed}
										onChange={e => setTyped(e.target.value)}
										disabled={isDeleting}
									/>
								</div>
							)}
						</>
					)}

					<footer className={css(styleSheet.footer)}>
						{isDeleting ? (
							<LoadingSpinner type='small' />
						) : (
							<>
								<button className={css(baseStyleSheet.ctaButtonReverse)} onClick={onClose}>
									Cancel
								</button>
								{mode === 'range' ? (
									<button className={css(baseStyleSheet.ctaButton)} onClick={submit} disabled={!canSubmitRange}>
										Show donations
									</button>
								) : (
									<button
										className={css(baseStyleSheet.ctaButtonDestructiveSolid)}
										onClick={submit}
										disabled={!canSubmitWipe}
									>
										Delete all donations
									</button>
								)}
							</>
						)}
					</footer>
				</div>
			</div>
		</Modal>
	);
};
